import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Trash2, Loader2, ShieldCheck, Eye, UserCog } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_META = {
  super_admin: { label: "Super Admin", icon: ShieldCheck, style: "bg-indigo-50 text-indigo-600 border-indigo-200" },
  client_admin: { label: "Admin", icon: UserCog, style: "bg-violet-50 text-violet-600 border-violet-200" },
  client_viewer: { label: "Viewer", icon: Eye, style: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function Team() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [members, setMembers] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client_viewer", client_id: "" });

  const load = useCallback(() => {
    const params = isSuper && clientFilter ? `?client_id=${clientFilter}` : "";
    api.get(`/users${params}`).then(({ data }) => setMembers(data)).catch(() => setMembers([]));
  }, [isSuper, clientFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isSuper) api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [isSuper]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isSuper) delete payload.client_id;
      if (isSuper && form.role !== "super_admin" && !form.client_id) {
        toast.error("Select a client for this member");
        setSaving(false);
        return;
      }
      await api.post("/users", payload);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "client_viewer", client_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Member removed");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">{isSuper ? "All users" : user?.client?.name}</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Team</h1>
          </div>
          <button onClick={() => setOpen(true)} data-testid="add-member-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm transition">
            <Plus className="w-4 h-4" /> Add member
          </button>
        </div>

        {isSuper && (
          <div className="inline-flex p-1 bg-slate-100/80 rounded-lg gap-1 border border-slate-200/50 flex-wrap">
            <button onClick={() => setClientFilter("")} className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${clientFilter === "" ? "bg-card text-indigo-600 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>All</button>
            {clients.map((c) => (
              <button key={c.id} onClick={() => setClientFilter(c.id)} className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${clientFilter === c.id ? "bg-card text-indigo-600 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>{c.name}</button>
            ))}
          </div>
        )}

        {members === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-indigo-600 animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="bg-card rounded-2xl border border-slate-100 p-12 text-center">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No team members yet.</p>
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-slate-100 shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Member</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                    {isSuper && <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>}
                    <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const meta = ROLE_META[m.role] || ROLE_META.client_viewer;
                    return (
                      <tr key={m.id} data-testid="member-row" className="border-t border-slate-50 hover:bg-indigo-50/30 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-indigo-800 text-indigo-50 flex items-center justify-center text-xs font-bold shrink-0">
                              {(m.name || m.email).slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800 truncate">{m.name}</p>
                              <p className="text-xs text-slate-400 truncate">{m.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border ${meta.style}`}>
                            <meta.icon className="w-3 h-3" /> {meta.label}
                          </span>
                        </td>
                        {isSuper && <td className="px-5 py-3.5 text-sm text-slate-600">{m.client_id ? clientName(m.client_id) : "—"}</td>}
                        <td className="px-5 py-3.5">
                          <span className={`text-xs font-medium ${m.status === "disabled" ? "text-slate-400" : "text-green-600"}`}>
                            {m.status === "disabled" ? "Disabled" : "Active"}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          {m.id !== user.id && (
                            <button onClick={() => setToDelete(m)} data-testid={`delete-member-${m.id}`} className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 transition">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add team member</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Name <span className="text-red-500">*</span></label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" className="tinp" placeholder="Full name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Email <span className="text-red-500">*</span></label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" className="tinp" placeholder="member@client.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Temporary password <span className="text-red-500">*</span></label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="member-password" className="tinp" placeholder="At least 6 characters" />
            </div>
            <div className={isSuper ? "grid grid-cols-2 gap-3" : ""}>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="member-role" className="tinp">
                  <option value="client_viewer">Viewer</option>
                  <option value="client_admin">Admin</option>
                  {isSuper && <option value="super_admin">Super Admin</option>}
                </select>
              </div>
              {isSuper && form.role !== "super_admin" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Client</label>
                  <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="member-client" className="tinp">
                    <option value="">Select...</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm">Cancel</button>
              <button type="submit" disabled={saving} data-testid="submit-member" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-indigo-50 font-semibold text-sm disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add member
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>They will lose access to COMO immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-member" className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </Layout>
  );
}
